import React, { useContext, useMemo } from "react";
import { useTheme } from "styled-components";
import { Clock3, TriangleAlert } from "lucide-react";
import {
  Direction,
  GridTradeGraphData,
  LanguageNameSpaces,
  TradeGraphData,
} from "@shared/types";
import { TradeDetailsContext } from "@shared/features/trades/TradeDetails.context.tsx";
import { TradeSource } from "@shared/features/trades/common/types";
import usePricesSocket from "@shared/services/socket/usePricesSocket";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { round } from "@shared/utils/numbers";
import { directionColor, getNewLookTokens } from "common/NewLook/tokens.ts";
import useValueFlash from "common/NewLook/useValueFlash.ts";
import {
  PROGRESS_LADDER_STYLE,
  ProgressCaption,
  ProgressFigures,
  ProgressHead,
  ProgressLabel,
  ProgressProfit,
  ProgressTime,
  ProgressWell,
} from "features/trades/NewLook/drawerDetails.ts";
import PriceLadder from "common/NewLook/PriceLadder.tsx";
import {
  getGridLadder,
  getOrdersLadder,
  PriceLadderModel,
} from "common/NewLook/priceLadder.ts";
import { TradeProgressTooltip } from "features/trades/common/TradeProgressTooltip";
import { useLadderCaption } from "features/trades/NewLook/useLadderCaption.ts";
import { formatSignedPercent } from "features/trades/NewLook/useMoneyFormat.ts";
import {
  formatTimeInTrade,
  useCachedHedgeLongShare,
  useLadderPriceFormat,
} from "features/trades/NewLook/useTradeLadder.ts";

/**
 * The trade drawer's progress: PnL %, time in trade, what is filled, and the
 * full price ladder built from the orders the drawer loads (every entry,
 * take-profit and the stop, labelled), a grid trade's range with its open
 * orders around the price, or a hedging leg's long/short split.
 */
const NewLookTradeProgress = () => {
  const theme = useTheme();
  const tokens = getNewLookTokens(theme);
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const {
    generalData,
    ordersData,
    statsData,
    gridCardsData,
    isGridBotTrade,
    tradeMetadata,
    symbol,
    exchange,
  } = useContext(TradeDetailsContext)!;
  const graphData = generalData?.data.graph_data as
    | (TradeGraphData & Partial<GridTradeGraphData>)
    | undefined;
  const isOpen = !!tradeMetadata?.trade_open;
  const isHedge = tradeMetadata?.source === TradeSource.HEDGING_BOT;
  const formatPrice = useLadderPriceFormat(symbol, exchange);
  const hedgeLongShare = useCachedHedgeLongShare(statsData?.id, isHedge);

  // The live price, when the socket's price is the pair's (it can be the
  // base coin's USD price for non-dollar pairs).
  const { socketPrice } = usePricesSocket(
    isOpen ? symbol : undefined,
    exchange
  );
  const loadedPrice =
    ordersData?.data.current_price ?? graphData?.current_price ?? undefined;
  const livePrice = socketPrice.value;
  const currentPrice =
    livePrice && (!loadedPrice || Math.abs(livePrice / loadedPrice - 1) < 0.5)
      ? livePrice
      : loadedPrice;

  const model = useMemo((): PriceLadderModel | undefined => {
    if (isGridBotTrade) {
      return graphData?.min_price !== undefined &&
        graphData.max_price !== undefined
        ? getGridLadder(
            { min_price: graphData.min_price, max_price: graphData.max_price },
            isOpen ? currentPrice : undefined,
            gridCardsData
          )
        : undefined;
    }
    if (isHedge) {
      const isShort =
        ordersData?.data.reverse ??
        generalData?.data.general?.["Long/Short"] === Direction.SHORT;
      return {
        type: "split",
        side: isShort ? Direction.SHORT : Direction.LONG,
        longShare: hedgeLongShare,
      };
    }
    return ordersData?.data
      ? getOrdersLadder(ordersData.data, {
          currentPrice,
          averageEntry: statsData?.entry_price,
          isOpen,
          closeStatus: generalData?.data.trade_status,
        })
      : undefined;
  }, [
    isGridBotTrade,
    isHedge,
    graphData,
    generalData,
    ordersData,
    statsData,
    gridCardsData,
    currentPrice,
    hedgeLongShare,
    isOpen,
  ]);

  const profit =
    graphData?.profit !== undefined
      ? round(graphData.profit || 0, 2)
      : undefined;
  const flash = useValueFlash(profit);
  if (!graphData) return null;

  return (
    <ProgressWell>
      <ProgressHead>
        <ProgressLabel>
          {t("table.headers.progress")}
          <TradeProgressTooltip isOpenTrades={isOpen} />
        </ProgressLabel>
        {graphData.time_passed && (
          <ProgressTime>
            <Clock3 size={12} strokeWidth={2} />
            {formatTimeInTrade(graphData.time_passed)}
          </ProgressTime>
        )}
      </ProgressHead>
      <ProgressFigures>
        {profit !== undefined && (
          <ProgressProfit
            className={flash}
            style={{ color: directionColor(tokens, profit) }}
          >
            {formatSignedPercent(profit)}
          </ProgressProfit>
        )}
        {model && <LadderCaption model={model} formatPrice={formatPrice} />}
      </ProgressFigures>
      {model && (
        <PriceLadder
          model={model}
          size="large"
          formatPrice={formatPrice}
          background={tokens.well}
          style={PROGRESS_LADDER_STYLE}
        />
      )}
    </ProgressWell>
  );
};

export default NewLookTradeProgress;

const LadderCaption = ({
  model,
  formatPrice,
}: {
  model: PriceLadderModel;
  formatPrice: (price: number) => string;
}) => {
  const caption = useLadderCaption(model, formatPrice);
  return (
    <ProgressCaption $isWarning={caption.isWarning}>
      {caption.isWarning && <TriangleAlert size={13} strokeWidth={2} />}
      {caption.text}
    </ProgressCaption>
  );
};
